function repairJsonStringLinebreaks(text) {
  let out = "";
  let inStr = false;
  let esc = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (!inStr) {
      if (ch === '"') {
        inStr = true;
        esc = false;
      }
      out += ch;
      continue;
    }

    if (esc) {
      out += ch;
      esc = false;
      continue;
    }
    if (ch === "\\") {
      out += ch;
      esc = true;
      continue;
    }
    if (ch === '"') {
      out += ch;
      inStr = false;
      continue;
    }

    // Drop raw line breaks that slipped into JSON strings during streaming.
    if (ch === "\n") continue;
    if (ch === "\r") {
      if (text[i + 1] === "\n") i++;
      continue;
    }

    out += ch;
  }

  return out;
}

function safeJsonParseWithRepair(text) {
  try {
    return JSON.parse(text);
  } catch {
    return JSON.parse(repairJsonStringLinebreaks(text));
  }
}

function findKeyOutsideStrings(src, key) {
  const needle = `"${key}"`;
  const len = src.length;
  let inStr = false;
  let quote = null;
  let esc = false;

  for (let i = 0; i < len; i++) {
    const ch = src[i];

    if (
      !inStr &&
      ch === '"' &&
      i + needle.length <= len &&
      src.slice(i, i + needle.length) === needle
    ) {
      return i;
    }

    if (inStr) {
      if (esc) {
        esc = false;
      } else if (ch === "\\") {
        esc = true;
      } else if (ch === quote) {
        inStr = false;
        quote = null;
      }
      continue;
    }

    if (ch === '"') {
      inStr = true;
      quote = ch;
    }
  }

  return -1;
}

function extractJsonStringValue(src, key) {
  const len = src.length;
  const keyIdx = findKeyOutsideStrings(src, key);
  if (keyIdx === -1) return null;

  let i = keyIdx + key.length + 2;
  for (; i < len; i++) {
    if (src[i] === ":") {
      i += 1;
      break;
    }
  }
  if (i >= len) return null;

  while (i < len && /\s/.test(src[i])) i += 1;
  if (src[i] !== '"') return null;

  const start = i;
  i += 1;
  let esc = false;

  for (; i < len; i++) {
    const ch = src[i];
    if (esc) {
      esc = false;
      continue;
    }
    if (ch === "\\") {
      esc = true;
      continue;
    }
    if (ch === '"') {
      const raw = src.slice(start, i + 1);
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
  }

  return null;
}

function findArrayStartForKey(src, key) {
  const keyIdx = findKeyOutsideStrings(src, key);
  if (keyIdx === -1) return -1;

  const len = src.length;
  let i = keyIdx + key.length + 2;
  for (; i < len; i++) {
    if (src[i] === ":") {
      i += 1;
      break;
    }
  }
  if (i >= len) return -1;

  while (i < len && /\s/.test(src[i])) i += 1;
  if (src[i] !== "[") return -1;
  return i;
}

function extractBalancedObjectsFromArray(src, arrayStart) {
  const objects = [];
  const len = src.length;
  let i = arrayStart + 1;
  let depth = 0;
  let inStr = false;
  let quote = null;
  let esc = false;
  let objStart = -1;

  for (; i < len; i++) {
    const ch = src[i];

    if (inStr) {
      if (esc) {
        esc = false;
      } else if (ch === "\\") {
        esc = true;
      } else if (ch === quote) {
        inStr = false;
        quote = null;
      }
      continue;
    }

    if (ch === '"') {
      inStr = true;
      quote = ch;
      continue;
    }

    if (ch === "{") {
      if (depth === 0) objStart = i;
      depth += 1;
      continue;
    }

    if (ch === "}") {
      if (depth > 0) depth -= 1;
      if (depth === 0 && objStart !== -1) {
        objects.push(src.slice(objStart, i + 1));
        objStart = -1;
      }
      continue;
    }

    if (ch === "]" && depth === 0) break;
  }

  return { objects, end: i };
}

function parseObjectArrayFromKey(src, key) {
  const start = findArrayStartForKey(src, key);
  if (start === -1) return [];

  const { objects } = extractBalancedObjectsFromArray(src, start);
  const parsed = [];

  for (const objText of objects) {
    try {
      const obj = safeJsonParseWithRepair(objText);
      if (obj && typeof obj === "object") parsed.push(obj);
    } catch {
      // ignore partial objects
    }
  }

  return parsed;
}

function normalizePartialTableSpec(spec) {
  if (!spec || typeof spec !== "object") return null;

  const columns = Array.isArray(spec.columns) ? spec.columns : [];
  const rows = Array.isArray(spec.rows) ? spec.rows : [];
  let normalizedColumns = columns;

  if (!normalizedColumns.length && rows.length) {
    const keys = Object.keys(rows[0] || {});
    if (keys.length) {
      normalizedColumns = keys.map((key) => ({ key, label: key }));
    }
  }

  return {
    ...spec,
    columns: normalizedColumns,
    rows,
  };
}

function normalizeTableSpecs(value) {
  const candidates = Array.isArray(value) ? value : [value];
  return candidates
    .map((candidate) => normalizePartialTableSpec(candidate))
    .filter(
      (candidate) =>
        candidate &&
        (candidate.columns?.length > 0 || candidate.rows?.length > 0),
    );
}

function splitMarkdownTableCells(line) {
  const raw = String(line ?? "").trim();
  if (!raw.includes("|")) return [];

  let cells = raw.split("|");
  if (cells.length && cells[0].trim() === "") cells = cells.slice(1);
  if (cells.length && cells[cells.length - 1].trim() === "") {
    cells = cells.slice(0, -1);
  }

  return cells.map((cell) => cell.trim());
}

function isMarkdownTableSeparatorLine(line, expectedColumns = null) {
  const cells = splitMarkdownTableCells(line);
  if (cells.length < 2) return false;
  if (expectedColumns != null && cells.length !== expectedColumns) return false;

  return cells.every((cell) => /^:?-+:?$/.test(cell.replace(/\s+/g, "")));
}

function markdownTableAlignmentFromCell(cell) {
  const normalized = String(cell ?? "").replace(/\s+/g, "");
  if (normalized.startsWith(":") && normalized.endsWith(":")) return "center";
  if (normalized.endsWith(":")) return "right";
  return "left";
}

function buildMarkdownTableSpec(headerCells, separatorCells, rowCells) {
  const columns = headerCells.map((label, idx) => ({
    key: `col_${idx}`,
    label: label || `Column ${idx + 1}`,
    align: markdownTableAlignmentFromCell(separatorCells[idx]),
  }));

  const rows = rowCells.map((cells) =>
    columns.reduce((acc, column, idx) => {
      acc[column.key] = cells[idx] ?? "";
      return acc;
    }, {}),
  );

  return { columns, rows };
}

function parseMarkdownTableTextSegments(text) {
  const src = String(text ?? "").replace(/\r\n/g, "\n");
  if (!src.trim() || src.indexOf("|") === -1) {
    return src.trim().length ? [{ type: "text", text: src }] : [];
  }

  const lines = src.split("\n");
  const segments = [];
  let textBuffer = [];

  const flushText = () => {
    const joined = textBuffer.join("\n");
    if (joined.trim().length) {
      segments.push({ type: "text", text: joined });
    }
    textBuffer = [];
  };

  let i = 0;
  while (i < lines.length) {
    const headerCells = splitMarkdownTableCells(lines[i]);
    const separatorLine = i < lines.length - 1 ? lines[i + 1] : "";
    const separatorCells = splitMarkdownTableCells(separatorLine);
    const hasTableStart =
      headerCells.length >= 2 &&
      headerCells.some((cell) => cell.length > 0) &&
      isMarkdownTableSeparatorLine(separatorLine, headerCells.length);

    if (!hasTableStart) {
      textBuffer.push(lines[i]);
      i += 1;
      continue;
    }

    const rowCells = [];
    let j = i + 2;
    while (j < lines.length) {
      const line = lines[j];
      if (!String(line ?? "").trim()) break;
      if (isMarkdownTableSeparatorLine(line, headerCells.length)) break;

      const cells = splitMarkdownTableCells(line);
      if (cells.length !== headerCells.length) break;
      rowCells.push(cells);
      j += 1;
    }

    if (rowCells.length === 0) {
      textBuffer.push(lines[i]);
      i += 1;
      continue;
    }

    flushText();
    segments.push({
      type: "table",
      spec: buildMarkdownTableSpec(headerCells, separatorCells, rowCells),
    });
    i = j;
  }

  flushText();
  return segments;
}

function parsePartialTableJson(body) {
  const src = String(body ?? "");
  if (!src.trim()) return [];
  const trimmed = src.trimStart();

  if (trimmed[0] === "[") {
    const { objects } = extractBalancedObjectsFromArray(trimmed, 0);
    const specs = [];

    for (const objText of objects) {
      try {
        const obj = safeJsonParseWithRepair(objText);
        specs.push(...normalizeTableSpecs(obj));
      } catch {
        // ignore partial objects
      }
    }

    return specs;
  }

  if (trimmed[0] !== "{") return [];

  try {
    const title = extractJsonStringValue(trimmed, "title");
    const columns = parseObjectArrayFromKey(trimmed, "columns");
    const rows = parseObjectArrayFromKey(trimmed, "rows");
    if (columns.length === 0 && rows.length === 0) return [];

    return normalizeTableSpecs({ title, columns, rows });
  } catch {
    return [];
  }
}

export function parseTableJsonSegments(srcInput) {
  const segments = [];
  const src = String(srcInput ?? "");
  if (!src) return segments;

  const TBL = "table-json";
  const FENCE = "```";
  const lower = src.toLowerCase();
  const len = src.length;

  function pushText(text) {
    if (!text.trim().length) return;
    parseMarkdownTableTextSegments(text).forEach((segment) => {
      segments.push(segment);
    });
  }

  function extractBalancedJsonValue(start) {
    if (start < 0 || start >= len) {
      return { complete: false, end: len, jsonText: "" };
    }

    const opener = src[start];
    if (opener !== "{" && opener !== "[") {
      return { complete: false, end: len, jsonText: "" };
    }

    const stack = [opener === "{" ? "}" : "]"];
    let inStr = false;
    let quote = null;
    let esc = false;

    for (let i = start + 1; i < len; i++) {
      const ch = src[i];

      if (inStr) {
        if (esc) {
          esc = false;
        } else if (ch === "\\") {
          esc = true;
        } else if (ch === quote) {
          inStr = false;
          quote = null;
        }
        continue;
      }

      if (ch === '"') {
        inStr = true;
        quote = ch;
        continue;
      }

      if (ch === "{") {
        stack.push("}");
        continue;
      }
      if (ch === "[") {
        stack.push("]");
        continue;
      }
      if ((ch === "}" || ch === "]") && stack[stack.length - 1] === ch) {
        stack.pop();
        if (stack.length === 0) {
          return {
            complete: true,
            end: i + 1,
            jsonText: src.slice(start, i + 1),
          };
        }
      }
    }

    return { complete: false, end: len, jsonText: src.slice(start) };
  }

  function findNextInline(from) {
    let pos = lower.indexOf(TBL, from);
    while (pos !== -1) {
      const before = pos === 0 ? " " : lower[pos - 1];
      const isWordBoundary = /\s|[>:([{,;]|^/.test(before);
      if (!isWordBoundary) {
        pos = lower.indexOf(TBL, pos + TBL.length);
        continue;
      }

      let k = pos + TBL.length;
      while (
        k < len &&
        (src[k] === " " ||
          src[k] === "\t" ||
          src[k] === ":" ||
          src[k] === "\n" ||
          src[k] === "\r")
      ) {
        k++;
      }

      if (src[k] === "{" || src[k] === "[") {
        return { tagIndex: pos, jsonStart: k };
      }

      pos = lower.indexOf(TBL, pos + TBL.length);
    }

    return null;
  }

  function findNextBareTableJson(from) {
    let inStr = false;
    let quote = null;
    let esc = false;

    for (let i = from; i < len; i++) {
      const ch = src[i];

      if (inStr) {
        if (esc) {
          esc = false;
        } else if (ch === "\\") {
          esc = true;
        } else if (ch === quote) {
          inStr = false;
          quote = null;
        }
        continue;
      }

      if (ch === '"') {
        inStr = true;
        quote = ch;
        continue;
      }

      if (ch !== "{" && ch !== "[") continue;

      const candidate = extractBalancedJsonValue(i);
      const specs = parsePartialTableJson(candidate.jsonText);
      if (specs.length) {
        return {
          jsonStart: i,
          complete: candidate.complete,
          end: candidate.end,
          jsonText: candidate.jsonText,
          specs,
        };
      }
    }

    return null;
  }

  function pushTablesFromSpecs(specs) {
    specs.forEach((spec) => {
      segments.push({ type: "table", spec });
    });
  }

  let i = 0;
  while (i < len) {
    const nextFence = src.indexOf(FENCE, i);
    const nextInline = findNextInline(i);
    const nextBare = findNextBareTableJson(i);

    const nextTargets = [
      nextFence !== -1 ? { type: "fence", pos: nextFence } : null,
      nextInline ? { type: "inline", pos: nextInline.tagIndex } : null,
      nextBare ? { type: "bare", pos: nextBare.jsonStart } : null,
    ].filter(Boolean);

    if (nextTargets.length === 0) {
      pushText(src.slice(i));
      break;
    }

    nextTargets.sort((a, b) => a.pos - b.pos);
    const nextTarget = nextTargets[0];

    if (nextTarget.type === "fence") {
      const fencePos = nextFence;
      if (fencePos > i) {
        pushText(src.slice(i, fencePos));
      }

      const langLineStart = fencePos + FENCE.length;
      const nlAfterLang = src.indexOf("\n", langLineStart);
      if (nlAfterLang === -1) {
        segments.push({ type: "pending-code", lang: "text", body: "" });
        break;
      }

      let langToken = src
        .slice(langLineStart, nlAfterLang)
        .trim()
        .toLowerCase();
      let bodyStart = nlAfterLang + 1;

      if (!langToken) {
        let lineEnd = src.indexOf("\n", bodyStart);
        if (lineEnd === -1) lineEnd = len;

        const firstLine = src.slice(bodyStart, lineEnd).trim().toLowerCase();
        if (firstLine === TBL) {
          langToken = TBL;
          bodyStart = lineEnd + 1;

          while (
            bodyStart < len &&
            (src[bodyStart] === "\n" || src[bodyStart] === "\r")
          ) {
            if (src[bodyStart] === "\r" && src[bodyStart + 1] === "\n") {
              bodyStart += 2;
            } else {
              bodyStart += 1;
            }
          }
        }
      }

      const fenceEnd = src.indexOf(FENCE, bodyStart);
      const body =
        fenceEnd === -1 ? src.slice(bodyStart) : src.slice(bodyStart, fenceEnd);
      const specs = parsePartialTableJson(body);
      const shouldRenderAsTable = langToken === TBL || specs.length > 0;

      if (fenceEnd === -1) {
        if (shouldRenderAsTable) {
          segments.push({ type: "pending-table", lang: TBL, body, specs });
        } else {
          segments.push({
            type: "pending-code",
            lang: langToken || "text",
            body,
          });
        }
        break;
      }

      if (shouldRenderAsTable && specs.length) {
        pushTablesFromSpecs(specs);
      } else {
        segments.push({
          type: "code",
          lang: langToken || "text",
          code: body.replace(/\n$/, ""),
        });
      }

      i = fenceEnd + FENCE.length;
      continue;
    }

    if (nextTarget.type === "inline") {
      const { tagIndex, jsonStart } = nextInline;
      if (tagIndex > i) {
        pushText(src.slice(i, tagIndex));
      }

      const candidate = extractBalancedJsonValue(jsonStart);
      const specs = parsePartialTableJson(candidate.jsonText);
      if (!candidate.complete) {
        segments.push({
          type: "pending-table",
          lang: TBL,
          body: candidate.jsonText,
          specs,
        });
        i = candidate.end;
        break;
      }

      if (specs.length) {
        pushTablesFromSpecs(specs);
      } else {
        segments.push({ type: "code", lang: "json", code: candidate.jsonText });
      }

      i = candidate.end;
      continue;
    }

    if (nextBare.jsonStart > i) {
      pushText(src.slice(i, nextBare.jsonStart));
    }

    if (!nextBare.complete) {
      segments.push({
        type: "pending-table",
        lang: TBL,
        body: nextBare.jsonText,
        specs: nextBare.specs,
      });
      i = nextBare.end;
      break;
    }

    pushTablesFromSpecs(nextBare.specs);
    i = nextBare.end;
  }

  if (segments.length === 0 && src.trim().length) {
    segments.push({ type: "text", text: src });
  }

  return segments;
}

const SEGMENT_CACHE_MAX = 500;
const segmentCache = new Map();

export function getSegmentsCached(msgId, raw) {
  const prev = segmentCache.get(msgId);
  if (prev && prev.raw === raw) return prev.parsed;

  const parsed = parseTableJsonSegments(raw || "");
  segmentCache.set(msgId, { raw, parsed });

  if (segmentCache.size > SEGMENT_CACHE_MAX) {
    const firstKey = segmentCache.keys().next().value;
    segmentCache.delete(firstKey);
  }

  return parsed;
}
